/**
 * Counter Animation Script
 * Animates numbers from 0 to a specified target value
 * Target number can be specified via data-counter attribute (preferred), data-target, or text content
 */

const initCounter = () => {
  // Check if jQuery is available
  if (typeof jQuery === 'undefined') {
    console.warn('jQuery is required for counter functionality');
    return;
  }

  const $counters = jQuery('.js-counter');

  if ($counters.length === 0) {
    return;
  }

  // Options for Intersection Observer (trigger when element is visible)
  const observerOptions = {
    threshold: 0.5,
    rootMargin: '0px'
  };

  // Function to start counter animation with delay
  const startCounterWithDelay = ($element) => {
    // Skip if already animated or scheduled
    if ($element.data('counter-animated') || $element.data('counter-scheduled')) {
      return;
    }

    // Get delay from data-delay attribute or use default (500ms)
    const delay = parseInt($element.data('delay')) || 500;

    // Mark as scheduled to prevent multiple delays
    $element.data('counter-scheduled', true);

    // Start animation after delay
    setTimeout(() => {
      animateCounter($element);
    }, delay);
  };

  // Function to animate counter
  const animateCounter = ($element) => {
    // Skip if already animated
    if ($element.data('counter-animated')) {
      return;
    }

    // Get target number from data-counter (preferred), data-target (fallback), or text content
    let target = parseFloat($element.data('counter'));
    
    if (isNaN(target)) {
      // Try data-target as fallback
      target = parseFloat($element.data('target'));
    }
    
    if (isNaN(target)) {
      // Try to parse from text content as last resort
      const textValue = $element.text().trim().replace(/[^\d.-]/g, '');
      target = parseFloat(textValue);
    }

    if (isNaN(target) || target <= 0) {
      console.warn('Counter target not found or invalid:', $element);
      return;
    }

    // Store original target
    $element.data('counter-target', target);
    
    // Set starting value
    let current = 0;
    
    // Determine duration (can be customized via data-duration attribute)
    const duration = parseInt($element.data('duration')) || 2000; // Default 2 seconds
    
    // Determine increment step based on target and duration
    const increment = target / (duration / 16); // 60fps animation
    const startTime = Date.now();

    // Animation function
    const updateCounter = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Use easing function for smoother animation (ease-out)
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      current = Math.floor(target * easeOutQuart);

      // Update element text
      $element.text(current);

      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      } else {
        // Ensure we end on the exact target
        $element.text(target);
        $element.data('counter-animated', true);
        
        // Trigger custom event
        $element.trigger('counterComplete', [target]);
      }
    };

    // Start animation
    requestAnimationFrame(updateCounter);
  };

  // Create Intersection Observer to trigger animation when element is visible
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const $element = jQuery(entry.target);
          // Start counter with delay after section becomes visible
          startCounterWithDelay($element);
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe each counter element
    $counters.each(function() {
      observer.observe(this);
    });
  } else {
    // Fallback for older browsers - animate with delay
    $counters.each(function() {
      startCounterWithDelay(jQuery(this));
    });
  }
};

export { initCounter };

